import type {
  Lobby,
  LobbyAPI,
  LobbyCategory,
  LobbyCategoryAPI,
  LobbyCategoryDisplay,
  LobbyCategoryDisplayAPI,
  LobbyGame,
  PageDisplay,
  PageDisplayAPI,
  SearchTag,
  SearchTagAPI,
  Studio,
  StudioAPI,
  Tag,
  TagAPI,
} from '../../../../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import App from '../../../../../app';

/**
 * Map tags
 */
export function mapTags(tags: TagAPI[]): Tag[] {
  return tags.map((tag) => ({
    name: tag.name,
    slug: tag.slug,
  }));
}

/**
 * Map search tags
 */
export function mapSearchTags(searchTags: SearchTagAPI[]): SearchTag[] {
  return searchTags.map((searchTag) => ({
    name: searchTag.name,
    slug: searchTag.slug,
  }));
}

/**
 * Map game
 */
export function mapGame(game: LobbyGame): LobbyGame {
  return {
    ...game,
    img: game.img !== null ? App.instance.content.getImgUrl(game.img) : null,
    imgMobile: game.imgMobile !== undefined ? App.instance.content.getImgUrl(game.imgMobile) : undefined,
    vid: game.vid !== undefined ? App.instance.content.getImgUrl(game.vid) : undefined,
  };
}

/**
 * Map games
 */
export function mapGames(games: LobbyGame[]): LobbyGame[] {
  return games.map((game) => mapGame(game));
}

/**
 * Map lobby category display
 */
export function mapLobbyCategoryDisplay(display: LobbyCategoryDisplayAPI): LobbyCategoryDisplay {
  // Get rid of unused properties
  const { showLabels, ...rest } = display;
  return {
    ...rest,
    shouldShowLabels: showLabels,
    // showVideo: display.showVideo, // TODO: rename to shouldShowVideo like we did with shouldShowLabels
  };
}

/**
 * Map page display
 */
export function mapPageDisplay(display: PageDisplayAPI): PageDisplay {
  const { showLabels, ...rest } = display;
  return {
    ...rest,
    shouldShowLabels: showLabels,
  };
}

/**
 * Map categories and only keep categories that have games or are user-based
 */
export function mapCategories(categories: LobbyCategoryAPI[]): LobbyCategory[] {
  return categories
    .filter(
      (category) => category.userBased || (category.gamesTotalCount !== undefined && category.gamesTotalCount > 0),
    )
    .map((category) => ({
      ...category,
      display: mapLobbyCategoryDisplay(category.display),
      displayMobile: category.displayMobile !== undefined ? mapLobbyCategoryDisplay(category.displayMobile) : undefined,
      page: mapPageDisplay(category.page),
      pageMobile: mapPageDisplay(category.pageMobile),
      filterId: category.filterId,
      games: category.games !== undefined ? mapGames(category.games) : undefined,
      gamesTotalCount: category.gamesTotalCount!,
      isUserBased: category.userBased,
      slug: category.slug,
    }));
}

/**
 * Map studios and remove studios that have no games
 */
export function mapStudios(studios: StudioAPI[]): Studio[] {
  return studios
    .filter((studio) => studio.gamesTotalCount !== undefined && studio.gamesTotalCount > 0)
    .map(
      (studio) =>
        ({
          ...studio,
          icon: studio.icon !== undefined ? App.instance.content.getImgUrl(studio.icon) : undefined,
          img: studio.img !== undefined ? App.instance.content.getImgUrl(studio.img) : undefined,
        }) as Studio,
    );
}

/**
 * Map lobby
 */
export function mapLobby(lobby: LobbyAPI): Lobby {
  return {
    id: lobby.id,
    categories: mapCategories(lobby.categories),
    searchCategories: mapCategories(lobby.searchCategories),
    searchTags: lobby.searchTags ? mapSearchTags(lobby.searchTags) : [],
    studios: mapStudios(lobby.studios),
    tags: mapTags(lobby.tags),
  };
}
